/**
 * SpeedyShip Content Configuration
 * All text content, labels, and copy for the website
 */

export const businessInfo = {
  name: 'SpeedyShip',
  tagline: 'Shipping made instant',
  description: 'Reliable last-mile delivery solutions across British Columbia. We specialize in same-day courier, scheduled routes, medical logistics, and B2B distribution — delivering speed, precision, and accountability with every shipment.',

  contact: {
    phone: '1-877-589-0573',
    phoneFormatted: '1-877-589-0573',
    whatsappNumber: '16043960469', // WhatsApp uses the legacy direct line (toll-free 1-877 not supported)
    email: 'dispatch@speedyship.ca',
    address: {
      street: '300-13888 Wireless Way',
      city: 'Richmond',
      province: 'BC',
      postalCode: 'V6V 3A4',
      full: '300-13888 Wireless Way, Richmond, BC, V6V 3A4',
    },
  },

  // CRA / tax registration — must appear on every customer-facing invoice for
  // GST/HST compliance. Source of truth lives here; UI also reads from
  // system_settings.gst_number when present (so admin Settings can override).
  taxRegistration: {
    gstNumber: '765437306 RT0001',
  },

  hours: {
    weekdays: 'Mon-Fri: 8:00 AM - 5:00 PM',
    saturday: 'Sat: 9:00 AM - 4:00 PM',
    sunday: 'Sun: Emergency service available',
    dispatch: 'Dispatch available 24/7',
  },

  social: {
    facebook: '',  // To be added
    twitter: '',   // To be added
    instagram: '', // To be added
    linkedin: '',  // To be added
    youtube: '',   // To be added
  },
} as const;

export const navigation = {
  main: [
    { label: 'Home', href: '/' },
    { label: 'Services', href: '/services' },
    { label: 'Coverage', href: '/coverage' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'How It Works', href: '/how-it-works' },
    { label: 'Business', href: '/business' },
    { label: 'Blog', href: '/blog' },
    { label: 'Contact', href: '/contact' },
  ],

  footer: {
    quickLinks: [
      { label: 'Services', href: '/services' },
      { label: 'Coverage', href: '/coverage' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'How It Works', href: '/how-it-works' },
      { label: 'Business', href: '/business' },
      { label: 'Blog', href: '/blog' },
      { label: 'Contact Us', href: '/contact' },
      { label: 'Track an Order', href: '/track' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
    ],
  },
} as const;

export const buttons = {
  primary: 'Ship Now',
  secondary: 'Call Dispatch',
  placeOrder: 'Place Order',
  bookNow: 'Book Now',
  openAccount: 'Open Business Account',
  letsChat: 'Let\'s Chat',
  backToHome: 'Back to home',
  submit: 'Submit',
  getQuote: 'Get Quote',
} as const;

export const hero = {
  title: 'Quick & Easy Shipping',
  subtitle: 'Book instantly. No waiting. No delays.',
  description: 'SpeedyShip is a trusted courier company that helps businesses deliver packages quickly and safely. We focus on last-mile delivery in British Columbia, making sure parcels reach customers on time.',
  cta: {
    primary: buttons.primary,
    secondary: buttons.openAccount,
  },
} as const;

export const services = {
  title: 'Our Services',
  subtitle: 'End-to-End Logistics Across British Columbia',
  description: 'SpeedyShip delivers integrated courier, freight, and warehousing solutions designed for speed, precision, and scalability.',

  list: [
    {
      id: 'standard-delivery',
      icon: 'truck',
      title: 'Standard Delivery',
      shortDescription: 'Reliable next-business-day delivery across British Columbia for regular shipments.',
      description: 'Next-Business-Day Across BC\nReliable next-business-day delivery across British Columbia for regular shipments.',
    },
    {
      id: 'rush-delivery',
      icon: 'zap',
      title: 'Rush Delivery',
      shortDescription: 'Priority delivery within 3-4 hours for urgent packages.',
      description: '3–4 Hour Priority Service\nPriority delivery within 3-4 hours for urgent packages.',
    },
    {
      id: 'direct-delivery',
      icon: 'navigation',
      title: 'Direct Delivery',
      shortDescription: 'Non-stop transport from pickup to destination with no transfers.',
      description: 'Non-Stop, Point-to-Point\nNon-stop transport from pickup to destination with no transfers.',
    },
    {
      id: 'medical-transport',
      icon: 'heart',
      title: 'Medical Transport',
      shortDescription: 'Secure and last-mile delivery for medical supplies, lab samples, and healthcare shipments.',
      description: 'Secure & Time-Critical Handling\nSecure and last-mile delivery for medical supplies, lab samples, and healthcare shipments.',
    },
    {
      id: 'temperature-controlled',
      icon: 'thermometer',
      title: 'Temperature Controlled',
      shortDescription: 'Cold-chain transport for pharmaceuticals and perishables.',
      description: 'Cold Chain Logistics\nCold-chain transport for pharmaceuticals and perishables.',
    },
    {
      id: 'freight-logistics',
      icon: 'box',
      title: 'Freight Logistics',
      shortDescription: 'Bulk, pallet, and high-volume deliveries across BC.',
      description: 'Bulk & Pallet Shipments\nBulk, pallet, and high-volume deliveries across BC.',
    },
    {
      id: 'warehousing',
      icon: 'warehouse',
      title: 'Warehousing',
      shortDescription: 'Secure warehousing solutions.',
      description: 'Storage. Inventory. Distribution.\nSecure warehousing',
    },
  ],
} as const;

export const whyChooseUs = {
  title: 'Why Choose SpeedyShip as Your BC Courier Partner?',
  features: [
    {
      icon: 'mapPin',
      title: 'Real-Time GPS Tracking',
      description: 'Our advanced GPS tracking logistics in British Columbia allow you to monitor your shipment at every step. From pickup to final delivery, you receive real-time updates and accurate delivery times. This helps businesses stay informed and gives customers peace of mind.',
    },
    {
      icon: 'thermometer',
      title: 'Temperature-Controlled Shipping',
      description: 'Some items require extra care during transport. Our temperature-controlled shipping in British Columbia ensures that sensitive goods like medical supplies, food items, and fragile products are handled safely. Our trained drivers and specialized vehicles help keep your packages protected throughout the journey.',
    },
    {
      icon: 'leaf',
      title: 'Eco-Friendly Delivery Services',
      description: 'SpeedyShip also focuses on sustainability. Our eco-friendly delivery in British Columbia uses smart route planning and efficient driving practices to reduce fuel use and lower emissions. This helps protect the environment while providing fast and reliable courier services.',
    },
    {
      icon: 'truck',
      title: 'Fleet Flexibility & Scalable Capacity',
      description: 'Our diverse fleet handles everything from small parcels to large bulk shipments. Whether you need delivery for same-day courier in British Columbia or scheduled distribution, our operations can easily scale to meet your demand during busy seasons.',
    },
    {
      icon: 'network',
      title: 'Technology-Driven Logistics Network',
      description: 'Our digital dispatch system helps manage orders, assign drivers, and confirm deliveries quickly. By using modern technology, we improve efficiency and maintain a high level of service for businesses across British Columbia.',
    },
    {
      icon: 'headphones',
      title: '24/7 Customer Support',
      description: 'Our team is always ready to help. With 24/7 customer support, we help with urgent shipments and delivery updates. SpeedyShip works around the clock to keep your business deliveries running smoothly.',
    },
  ],
} as const;

export const howItWorks = {
  title: 'Speed and Precision in Every Delivery',
  subtitle: 'Speedyship makes last-mile delivery effortless across British Columbia. From booking to proof of delivery — we handle everything with speed and precision.',

  steps: [
    {
      number: 1,
      title: 'Book Your Delivery',
      description: 'Schedule your pickup in seconds. Choose same-day, scheduled, medical, or hot shot delivery.',
    },
    {
      number: 2,
      title: 'We Pick Up',
      description: 'Our professional drivers arrive on time and secure your shipment for transit.',
    },
    {
      number: 3,
      title: 'Quick Delivery',
      description: 'Fast, optimized routes ensure safe and on-time delivery — every time.',
    },
    {
      number: 4,
      title: 'Delivery Confirmation',
      description: 'Instant digital confirmation with signature and time stamp for complete transparency.',
    },
  ],
} as const;

export const coverage = {
  title: 'Coverage and Routes we serve',
  subtitle: 'Fast Delivery Across British Columbia',
  description: 'SpeedyShip provides same-day and next-day courier services across the Lower Mainland and key BC cities.',
} as const;

export const businessAccounts = {
  title: 'Need Regular Deliveries?',
  subtitle: 'SpeedyShip supports businesses across British Columbia with reliable scheduled and on-demand delivery. From retail and healthcare to legal and multi-location operations, we\'re your trusted last-mile partner.',

  features: [
    'Priority dispatch for delivery orders',
    'Real-time shipment tracking and reports',
    'Simple weekly/monthly billing',
    'Dedicated account support',
    'Preferred pricing based on volume',
    'Delivery proof with digital signatures',
  ],
} as const;

export const speedPrecision = {
  title: 'SPEED MEETS PRECISION',
  description: 'SpeedyShip supports businesses across British Columbia with reliable scheduled and on-demand delivery. From retail and healthcare to legal and multi-location operations, we\'re your trusted last-mile partner.',
} as const;

export const whatsapp = {
  title: 'Chat with Us on WhatsApp',
  subtitle: 'Instant support, just one message away',
  buttonText: buttons.letsChat,
  link: `https://wa.me/${businessInfo.contact.whatsappNumber}`,
} as const;

export const footer = {
  copyright: '© 2026 SpeedyShip | Powered By <a href="https://mavitdigital.com/" target="_blank" rel="noopener noreferrer" class="hover:text-white transition-colors">Mavit Digital</a>',
  description: businessInfo.description,
} as const;

export const errorPages = {
  404: {
    title: '404',
    heading: 'Looks like you are lost.',
    message: 'We can\'t seem to find the page you\'re looking for.',
    cta: buttons.backToHome,
  },
} as const;

export const contact = {
  title: 'Get in touch',
  subtitle: 'We are here to answer any question you may have. Feel free to reach via contact form.',

  sections: {
    connect: {
      title: 'WANTS TO CONNECT?',
      value: businessInfo.contact.email,
    },
    phone: {
      title: 'CALL US AT',
      value: businessInfo.contact.phone,
    },
    location: {
      title: 'OUR LOCATION',
      value: businessInfo.contact.address.full,
    },
    hours: {
      title: 'BUSINESS HOURS',
      value: `${businessInfo.hours.weekdays}\n${businessInfo.hours.saturday}\n${businessInfo.hours.sunday}\n${businessInfo.hours.dispatch}`,
    },
  },
} as const;

export default {
  businessInfo,
  navigation,
  buttons,
  hero,
  services,
  whyChooseUs,
  howItWorks,
  coverage,
  businessAccounts,
  speedPrecision,
  whatsapp,
  footer,
  errorPages,
  contact,
};
